import { useState, useRef, useCallback, useEffect } from 'react';

export interface OpenAIRealtimeConfig {
  model: string;
  instructions: string;
  voice: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
  modalities: ('text' | 'audio')[];
  input_audio_format: 'pcm16' | 'g711_ulaw' | 'g711_alaw';
  output_audio_format: 'pcm16' | 'g711_ulaw' | 'g711_alaw';
  input_audio_transcription: {
    model: 'whisper-1';
  };
  turn_detection: {
    type: 'server_vad';
    threshold: number;
    prefix_padding_ms: number;
    silence_duration_ms: number;
  };
  firstMessage?: string;
}

export interface OpenAIRealtimeStartParams {
  config: OpenAIRealtimeConfig;
  clientSecret: string; // Ephemeral token from backend
}

export type OpenAIRealtimeStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error';

export interface OpenAIRealtimeMessage {
  source: 'user' | 'ai';
  message: string;
  timestamp: number;
}

export interface OpenAIRealtimeToolCall {
  name: string;
  callId: string;
  arguments: Record<string, unknown>;
}

export interface OpenAIRealtimeToolResult {
  success: boolean;
  message: string;
}

interface UseOpenAIRealtimeOptions {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: string) => void;
  onMessage?: (message: OpenAIRealtimeMessage) => void;
  onSpeakingChange?: (isSpeaking: boolean) => void;
  onToolCall?: (
    toolCall: OpenAIRealtimeToolCall,
  ) => Promise<OpenAIRealtimeToolResult>;
}

interface UseOpenAIRealtimeReturn {
  status: OpenAIRealtimeStatus;
  isSpeaking: boolean;
  isWaiting: boolean;
  startSession: (params: OpenAIRealtimeStartParams) => Promise<void>;
  endSession: () => Promise<void>;
  sendContextualUpdate: (text: string) => void;
  injectContextAndRespond: (text: string) => void;
  injectContextSilently: (text: string) => void;
  setWaitingMode: (waiting: boolean) => void;
  getInputByteFrequencyData: () => Uint8Array | null;
  getOutputByteFrequencyData: () => Uint8Array | null;
}

export function useOpenAIRealtime(
  options: UseOpenAIRealtimeOptions = {},
): UseOpenAIRealtimeReturn {
  const {
    onConnect,
    onDisconnect,
    onError,
    onMessage,
    onSpeakingChange,
    onToolCall,
  } = options;

  const [status, setStatus] = useState<OpenAIRealtimeStatus>('disconnected');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const analyserInputRef = useRef<AnalyserNode | null>(null);
  const analyserOutputRef = useRef<AnalyserNode | null>(null);

  // Audio playback scheduling - track when next audio should start
  const nextPlayTimeRef = useRef<number>(0);

  // Track active audio sources for interruption handling
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const currentItemIdRef = useRef<string | null>(null);
  const playedAudioMsRef = useRef<number>(0);
  const responseStartTimeRef = useRef<number>(0);

  // Track current response ID to ignore events from cancelled responses
  const currentResponseIdRef = useRef<string | null>(null);
  const cancelledResponseIdsRef = useRef<Set<string>>(new Set());

  // Waiting mode refs
  const isWaitingRef = useRef(false);
  const waitingAudioRef = useRef<HTMLAudioElement | null>(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.disconnect();
      audioWorkletNodeRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    nextPlayTimeRef.current = 0;
    activeSourcesRef.current = [];
    currentItemIdRef.current = null;
    playedAudioMsRef.current = 0;
    responseStartTimeRef.current = 0;
    currentResponseIdRef.current = null;
    cancelledResponseIdsRef.current = new Set();

    // Stop waiting audio if playing
    if (waitingAudioRef.current) {
      waitingAudioRef.current.pause();
      waitingAudioRef.current = null;
    }
    isWaitingRef.current = false;
    setIsWaiting(false);
  }, []);

  // Start OpenAI Realtime session
  const startSession = useCallback(
    async (params: OpenAIRealtimeStartParams) => {
      const { config, clientSecret } = params;

      try {
        setStatus('connecting');
        cleanup();

        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 24000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        mediaStreamRef.current = stream;

        // Create audio context
        const audioContext = new AudioContext({ sampleRate: 24000 });
        audioContextRef.current = audioContext;

        // Create analysers for input/output visualization
        analyserInputRef.current = audioContext.createAnalyser();
        analyserInputRef.current.fftSize = 256;

        analyserOutputRef.current = audioContext.createAnalyser();
        analyserOutputRef.current.fftSize = 256;

        // Connect microphone to input analyser
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyserInputRef.current);

        // Connect to OpenAI Realtime API using ephemeral client secret.
        // The client secret is passed as a subprotocol for authentication.
        // Note: the legacy `openai-beta.realtime-v1` subprotocol was deprecated
        // on May 18, 2026 alongside the gpt-4o-*-preview model family. The
        // current GA Realtime API (gpt-realtime / gpt-realtime-2) only requires
        // `realtime` + the ephemeral-key subprotocol.
        const ws = new WebSocket(
          `wss://api.openai.com/v1/realtime?model=${config.model}`,
          ['realtime', `openai-insecure-api-key.${clientSecret}`],
        );

        wsRef.current = ws;

        ws.onopen = () => {
          console.log('[OpenAI Realtime] WebSocket connected');

          // Session is already configured via the backend session creation
          // We just need to signal we're ready
          // The session.update was already done when we got the client secret
          setStatus('connected');
          onConnect?.();

          // Start sending audio from microphone
          startAudioCapture(audioContext, stream, ws);

          // If there's a first message, trigger agent to speak
          if (config.firstMessage) {
            ws.send(
              JSON.stringify({
                type: 'response.create',
                response: {
                  modalities: config.modalities,
                  instructions: config.firstMessage,
                },
              }),
            );
          }
        };

        ws.onmessage = async (event) => {
          try {
            const data = JSON.parse(event.data);

            switch (data.type) {
              case 'session.created':
                console.log('[OpenAI Realtime] Session created');
                // Session is pre-configured via backend, connection is handled in onopen
                break;

              case 'session.updated':
                console.log('[OpenAI Realtime] Session updated');
                // Session updates are handled, but connection is already established in onopen
                break;

              case 'conversation.item.input_audio_transcription.completed':
                // User speech transcribed
                if (data.transcript) {
                  onMessage?.({
                    source: 'user',
                    message: data.transcript,
                    timestamp: Date.now(),
                  });
                }
                break;

              case 'response.created':
                // Track the response ID for cancellation detection
                if (data.response?.id) {
                  currentResponseIdRef.current = data.response.id;
                }
                break;

              case 'response.audio.delta': {
                // Ignore audio from cancelled responses
                const responseId = data.response_id;
                if (
                  responseId &&
                  cancelledResponseIdsRef.current.has(responseId)
                ) {
                  break;
                }
                // Streaming audio from AI
                if (data.delta) {
                  // Decode base64 audio and schedule for sequential playback
                  const audioData = base64ToArrayBuffer(data.delta);
                  playAudio(audioContext, audioData);
                }
                break;
              }

              case 'response.audio_transcript.delta':
                // Streaming text transcript from AI
                break;

              case 'response.audio_transcript.done': {
                // Ignore transcripts from cancelled responses
                const transcriptResponseId = data.response_id;
                if (
                  transcriptResponseId &&
                  cancelledResponseIdsRef.current.has(transcriptResponseId)
                ) {
                  console.log(
                    '[OpenAI Realtime] Ignoring transcript from cancelled response',
                  );
                  break;
                }
                // AI finished speaking - full transcript available
                if (data.transcript) {
                  onMessage?.({
                    source: 'ai',
                    message: data.transcript,
                    timestamp: Date.now(),
                  });
                }
                break;
              }

              case 'response.output_item.added':
                // Track the item ID for truncation and reset audio scheduling
                if (data.item?.id) {
                  currentItemIdRef.current = data.item.id;
                }
                nextPlayTimeRef.current = audioContext.currentTime;
                responseStartTimeRef.current = Date.now();
                playedAudioMsRef.current = 0;
                activeSourcesRef.current = [];
                setIsSpeaking(true);
                onSpeakingChange?.(true);
                break;

              case 'input_audio_buffer.speech_started': {
                // User started speaking - stop all audio playback immediately
                console.log(
                  '[OpenAI Realtime] User interrupted, stopping playback',
                );

                // Mark current response as cancelled to ignore any remaining events
                if (currentResponseIdRef.current) {
                  cancelledResponseIdsRef.current.add(
                    currentResponseIdRef.current,
                  );
                  // Cap the set size to prevent unbounded growth
                  if (cancelledResponseIdsRef.current.size > 50) {
                    const first = cancelledResponseIdsRef.current
                      .values()
                      .next().value;
                    if (first) cancelledResponseIdsRef.current.delete(first);
                  }
                  console.log(
                    `[OpenAI Realtime] Marked response ${currentResponseIdRef.current} as cancelled`,
                  );
                }

                activeSourcesRef.current.forEach((source) => {
                  try {
                    source.stop();
                  } catch {
                    // Source may have already ended
                  }
                });
                activeSourcesRef.current = [];

                // Calculate how much audio was actually played
                const elapsedMs = Date.now() - responseStartTimeRef.current;
                playedAudioMsRef.current = Math.max(0, elapsedMs);

                // Send truncation event to server if we have an item ID
                if (
                  currentItemIdRef.current &&
                  ws.readyState === WebSocket.OPEN
                ) {
                  ws.send(
                    JSON.stringify({
                      type: 'conversation.item.truncate',
                      item_id: currentItemIdRef.current,
                      content_index: 0,
                      audio_end_ms: playedAudioMsRef.current,
                    }),
                  );
                  console.log(
                    `[OpenAI Realtime] Sent truncate for ${currentItemIdRef.current} at ${playedAudioMsRef.current}ms`,
                  );
                }

                setIsSpeaking(false);
                onSpeakingChange?.(false);
                break;
              }

              case 'response.function_call_arguments.done': {
                // Model wants to call a tool - check if response was cancelled
                const toolResponseId = data.response_id;
                if (
                  toolResponseId &&
                  cancelledResponseIdsRef.current.has(toolResponseId)
                ) {
                  console.log(
                    '[OpenAI Realtime] Ignoring tool call from cancelled response',
                  );
                  break;
                }

                console.log('[OpenAI Realtime] Tool call received:', data.name);

                if (onToolCall) {
                  try {
                    // Parse the arguments
                    const args = JSON.parse(data.arguments || '{}');

                    // Call the handler and get the result
                    const result = await onToolCall({
                      name: data.name,
                      callId: data.call_id,
                      arguments: args,
                    });

                    // Send the result back to OpenAI
                    if (ws.readyState === WebSocket.OPEN) {
                      ws.send(
                        JSON.stringify({
                          type: 'conversation.item.create',
                          item: {
                            type: 'function_call_output',
                            call_id: data.call_id,
                            output: JSON.stringify(result),
                          },
                        }),
                      );

                      // Trigger the model to continue responding
                      ws.send(
                        JSON.stringify({
                          type: 'response.create',
                        }),
                      );

                      console.log(
                        '[OpenAI Realtime] Sent tool result and triggered response',
                      );
                    }
                  } catch (err) {
                    console.error('[OpenAI Realtime] Tool call error:', err);
                    // Send error result back
                    if (ws.readyState === WebSocket.OPEN) {
                      ws.send(
                        JSON.stringify({
                          type: 'conversation.item.create',
                          item: {
                            type: 'function_call_output',
                            call_id: data.call_id,
                            output: JSON.stringify({
                              success: false,
                              message: 'Tool execution failed',
                            }),
                          },
                        }),
                      );
                      ws.send(
                        JSON.stringify({
                          type: 'response.create',
                        }),
                      );
                    }
                  }
                }
                break;
              }

              case 'response.cancelled':
                // Response was cancelled (usually due to interruption)
                console.log('[OpenAI Realtime] Response cancelled');
                setIsSpeaking(false);
                onSpeakingChange?.(false);
                break;

              case 'response.done':
                setIsSpeaking(false);
                onSpeakingChange?.(false);
                break;

              case 'error':
                // Suppress harmless truncate errors (happens when audio is shorter than truncate position)
                if (data.error?.message?.includes('already shorter than')) {
                  console.log(
                    '[OpenAI Realtime] Truncate skipped (audio already shorter)',
                  );
                } else {
                  console.error('[OpenAI Realtime] Error:', data.error);
                  onError?.(data.error?.message || 'Unknown error');
                }
                break;

              default:
                console.log('[OpenAI Realtime] Event:', data.type);
            }
          } catch (err) {
            console.error('[OpenAI Realtime] Error parsing message:', err);
          }
        };

        ws.onerror = (error) => {
          console.error('[OpenAI Realtime] WebSocket error:', error);
          setStatus('error');
          onError?.('WebSocket connection error');
        };

        ws.onclose = () => {
          console.log('[OpenAI Realtime] WebSocket closed');
          setStatus('disconnected');
          onDisconnect?.();
          cleanup();
        };
      } catch (err) {
        console.error('[OpenAI Realtime] Start session error:', err);
        setStatus('error');
        onError?.(
          err instanceof Error ? err.message : 'Failed to start session',
        );
        cleanup();
      }
    },
    [
      cleanup,
      onConnect,
      onDisconnect,
      onError,
      onMessage,
      onSpeakingChange,
      onToolCall,
    ],
  );

  // Start capturing and sending audio
  const startAudioCapture = (
    audioContext: AudioContext,
    stream: MediaStream,
    ws: WebSocket,
  ) => {
    // Create a ScriptProcessorNode (deprecated but widely supported)
    // In production, use AudioWorklet for better performance
    const bufferSize = 4096;
    const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(processor);
    processor.connect(audioContext.destination);

    processor.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      // Don't send audio when in waiting mode (uninterruptable)
      if (isWaitingRef.current) return;

      const inputData = e.inputBuffer.getChannelData(0);

      // Convert float32 to int16 PCM
      const pcm16 = new Int16Array(inputData.length);
      for (let i = 0; i < inputData.length; i++) {
        const s = Math.max(-1, Math.min(1, inputData[i]));
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }

      // Convert to base64 and send
      const base64 = arrayBufferToBase64(pcm16.buffer);
      ws.send(
        JSON.stringify({
          type: 'input_audio_buffer.append',
          audio: base64,
        }),
      );
    };
  };

  // Play received audio with proper scheduling to avoid overlap
  const playAudio = (audioContext: AudioContext, audioData: ArrayBuffer) => {
    try {
      // Convert PCM16 to float32
      const int16Array = new Int16Array(audioData);
      const float32Array = new Float32Array(int16Array.length);

      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768;
      }

      // Create audio buffer
      const audioBuffer = audioContext.createBuffer(
        1,
        float32Array.length,
        24000,
      );
      audioBuffer.getChannelData(0).set(float32Array);

      // Play the audio
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;

      // Connect to output analyser for visualization
      if (analyserOutputRef.current) {
        source.connect(analyserOutputRef.current);
        analyserOutputRef.current.connect(audioContext.destination);
      } else {
        source.connect(audioContext.destination);
      }

      // Schedule audio to play sequentially
      const currentTime = audioContext.currentTime;
      const startTime = Math.max(currentTime, nextPlayTimeRef.current);

      // Update next play time to after this chunk finishes
      nextPlayTimeRef.current = startTime + audioBuffer.duration;

      // Track this source for interruption handling
      activeSourcesRef.current.push(source);

      // Remove from active sources when done
      source.onended = () => {
        activeSourcesRef.current = activeSourcesRef.current.filter(
          (s) => s !== source,
        );
      };

      source.start(startTime);
    } catch (err) {
      console.error('[OpenAI Realtime] Error playing audio:', err);
    }
  };

  // End session
  const endSession = useCallback(async () => {
    cleanup();
    setStatus('disconnected');
    setIsSpeaking(false);
  }, [cleanup]);

  // Send contextual update (inject context mid-conversation without triggering response)
  const sendContextualUpdate = useCallback((text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // Use conversation.item.create to add context without triggering a new response
      // The model will see this in context for its next natural response
      wsRef.current.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'system',
            content: [
              {
                type: 'input_text',
                text: `[CONTEXT UPDATE]: ${text}`,
              },
            ],
          },
        }),
      );
      console.log(
        '[OpenAI Realtime] Sent contextual update (no response triggered)',
      );
    }
  }, []);

  // Inject context and trigger an interrupting response (for sync tool calls)
  const injectContextAndRespond = useCallback((text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // Stop any currently playing audio to prevent overlap
      activeSourcesRef.current.forEach((source) => {
        try {
          source.stop();
        } catch {
          // Source may already be stopped
        }
      });
      activeSourcesRef.current = [];
      nextPlayTimeRef.current = 0;
      setIsSpeaking(false);

      // Cancel any ongoing response
      wsRef.current.send(JSON.stringify({ type: 'response.cancel' }));

      // Add the context as a system message
      wsRef.current.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'system',
            content: [
              {
                type: 'input_text',
                text: `[ASYNC AGENT RESULT - INTERRUPT AND RESPOND TO USER]: ${text}`,
              },
            ],
          },
        }),
      );

      // Trigger a new response
      wsRef.current.send(JSON.stringify({ type: 'response.create' }));

      console.log('[OpenAI Realtime] Injected context and triggered response');
    }
  }, []);

  // Inject context silently without interrupting (for async tool calls)
  const injectContextSilently = useCallback((text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // Just add context as a system message - don't interrupt or trigger response
      wsRef.current.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'system',
            content: [
              {
                type: 'input_text',
                text: `[BACKGROUND TASK COMPLETED - USE THIS INFO WHEN RELEVANT]: ${text}`,
              },
            ],
          },
        }),
      );

      console.log('[OpenAI Realtime] Injected context silently (no interrupt)');
    }
  }, []);

  // Set waiting mode - mutes mic and plays looping piano sound
  const setWaitingMode = useCallback((waiting: boolean) => {
    isWaitingRef.current = waiting;
    setIsWaiting(waiting);

    if (waiting) {
      // Start looping piano sound
      const audio = new Audio('/waiting-piano.mp3');
      audio.loop = true;
      audio.volume = 0.3; // Keep it subtle
      audio.play().catch((err) => {
        console.warn('[OpenAI Realtime] Could not play waiting audio:', err);
      });
      waitingAudioRef.current = audio;
      console.log(
        '[OpenAI Realtime] Entered waiting mode - mic muted, playing wait sound',
      );
    } else {
      // Stop looping piano sound
      if (waitingAudioRef.current) {
        waitingAudioRef.current.pause();
        waitingAudioRef.current = null;
      }
      console.log('[OpenAI Realtime] Exited waiting mode - mic unmuted');
    }
  }, []);

  // Get frequency data for visualization
  const getInputByteFrequencyData = useCallback(() => {
    if (analyserInputRef.current) {
      const data = new Uint8Array(analyserInputRef.current.frequencyBinCount);
      analyserInputRef.current.getByteFrequencyData(data);
      return data;
    }
    return null;
  }, []);

  const getOutputByteFrequencyData = useCallback(() => {
    if (analyserOutputRef.current) {
      const data = new Uint8Array(analyserOutputRef.current.frequencyBinCount);
      analyserOutputRef.current.getByteFrequencyData(data);
      return data;
    }
    return null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    status,
    isSpeaking,
    isWaiting,
    startSession,
    endSession,
    sendContextualUpdate,
    injectContextAndRespond,
    injectContextSilently,
    setWaitingMode,
    getInputByteFrequencyData,
    getOutputByteFrequencyData,
  };
}

// Utility functions
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export default useOpenAIRealtime;
